import { PlanTypes } from '@chiroup/core/types/HomePage.type';
import type { MeClinic } from '@chiroup/core/types/Me.type';
import dayjs from 'dayjs';

type ClinicState = Pick<
  MeClinic,
  'ehr' | 'plan' | 'subscription' | 'billDate' | 'ehrGracePeriodEndDate'
>;

/**
 * Decide whether the app should redirect the user to /settings/subscription
 * because their clinic's subscription is in a state that blocks access.
 *
 * EHR clinics are intentionally not blocked while either:
 *  - their subscription is past_due (Stripe retry window), or
 *  - their grace period is still active post-cancellation
 * The /me endpoint flips ehr=0 once the grace date passes, which naturally
 * drops these exemptions and lets the legacy block resume.
 */
export const shouldBlockToBilling = (clinic: ClinicState | undefined | null): boolean => {
  if (!clinic) return false;

  const status = clinic.subscription?.status;
  const pastDue = status === 'past_due';
  const active = status === 'active';
  const trialing = status === 'trialing';
  const pastDueDays = clinic.subscription?.pastDueDays;
  const billDateHasNotPassed = dayjs(clinic.billDate) > dayjs();
  const requiresAction =
    (status === 'requires_action' || status === 'incomplete') && billDateHasNotPassed;
  const pastDueUnder7Days = pastDue && typeof pastDueDays === 'number' && pastDueDays < 7;
  const isEhrClinic = !!clinic.ehr && clinic.plan === PlanTypes.ehr;
  const inEhrGracePeriod = isEhrClinic && !!clinic.ehrGracePeriodEndDate;
  const isEhrPastDue = isEhrClinic && pastDue;

  return (
    !active &&
    !trialing &&
    !pastDueUnder7Days &&
    !requiresAction &&
    !inEhrGracePeriod &&
    !isEhrPastDue
  );
};

/**
 * Whether the generic past_due BillingBanner should be hidden because the
 * richer EhrGracePeriodBanner is taking over.
 */
export const shouldSuppressBillingBanner = (clinic: ClinicState | undefined | null): boolean => {
  if (!clinic) return false;

  return !!clinic.ehr && clinic.plan === PlanTypes.ehr && !!clinic.ehrGracePeriodEndDate;
};
